import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import _isNumber from 'lodash/isNumber';
import _isNaN from 'lodash/isNaN';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import pluralize from 'pluralize';
import {
  getCurrentTime,
  getServerTime,
  getTimeInHumanFormat,
  getTimeSpentInTest,
  isFirefoxBrowser,
  isIOSWebView,
} from '../../utils/utils';

import ConfirmModal, {
  ConfirmModel,
} from '../shared/ConfirmModal/ConfirmModal';
import { popUpClick, popUpView } from '../../utils/trackingUtil';
import { setAutoSubmitReason } from '../../actions/appState';
import {
  DPJ_TEST_TYPE_SESSIONSTORAGE_KEY,
  POPUP_NAME,
  TEST_START_TIME_SESSIONSTORAGE_KEY,
  AUTO_SUBMIT_REASONS,
} from '../../constants/Constants';
import { emitWebsocketEvent } from '../../actions/socket';
import { SOCKET_EVENTS } from '../../constants/Socket';
import { trackWindowBlur, trackWindowFocus } from '../../actions/eventTracking';
import { setTestEndTime } from '../../actions/test';
import { PROCTOR_TABID_SESSIONSTORAGE_KEY } from '../../constants/Proctor';
import { leaveTestViolationAction } from '../../actions/proctor';

export default function BrowserTolerance({
  test,
  auth,
  submitTest,
  testSolutionSet,
  showMultiSession,
  browserToleranceCount,
  clearClipboard,
  setFullScreen,
  showBrowserToleranceModal,
  hideBrowserToleranceModal,
  initializeBrowserTolerance,
  decrementBrowserToleranceCount,
  attemptedProblems,
  leaveTestViolation,
}) {
  const { isPodBasedProblem, showSystemCheck, isAiInterview } = useSelector(
    state => ({
      isPodBasedProblem: state.assessment?.isPodBased,
      showSystemCheck: state.appStatus?.showSystemCheck,
      isAiInterview: state.test?.isAiInterview,
    })
  );
  const [modalConfig, setModalConfig] = useState(new ConfirmModel());
  const { is_expired, is_feedback_enabled } = test;
  const toleranceCount = useRef();
  const attemptedProblemsCount = useRef();
  const timerRef = useRef(null);
  const isAutoSubmitInProcess = useRef();
  const assessmentIdRef = useRef();
  const systemCheckRef = useRef();
  const leaveTestViolationRef = useRef();
  const dispatch = useDispatch();

  assessmentIdRef.current = useSelector(state => state.assessment.assessmentId);
  systemCheckRef.current = showSystemCheck;
  leaveTestViolationRef.current = leaveTestViolation;

  const {
    showWarning,
    assessmentType,
    proctorEnabled,
    actualBrowserToleranceCount,
    startTime,
  } = useMemo(() => {
    // added data in session storage as in iframe related
    // events, reducers value were not accessible
    const testStartTime = testSolutionSet?.start_time;
    if (testStartTime) {
      sessionStorage.setItem(
        DPJ_TEST_TYPE_SESSIONSTORAGE_KEY,
        JSON.stringify(isPodBasedProblem)
      );
    }
    sessionStorage.setItem(TEST_START_TIME_SESSIONSTORAGE_KEY, testStartTime);
    return {
      showWarning: _get(test, 'settings.proctor.browser_tolerance.warning'),
      assessmentType: _get(test, 'test_type_verbose', ''),
      proctorEnabled:
        !!_get(test, 'settings.proctor.enabled') &&
        !!_get(testSolutionSet, 'id'),
      actualBrowserToleranceCount:
        test?.settings?.proctor?.browser_tolerance?.count,
      startTime: testStartTime,
    };
  }, [test, testSolutionSet, isPodBasedProblem]);

  isAutoSubmitInProcess.current = test.isTestSubmitted;

  const isFullScreenDisabled = () => {
    const isIOSDevice = isIOSWebView();
    if (isIOSDevice) {
      return false;
    }
    return (
      document.fullscreen === false ||
      document.mozFullScreen === false ||
      document.webkitIsFullScreen === false ||
      document.fullscreenElement === null
    );
  };

  attemptedProblemsCount.current = useMemo(() => {
    return _keys(attemptedProblems).length;
  }, [attemptedProblems]);

  toleranceCount.current = useMemo(() => {
    if (_isNumber(browserToleranceCount) && !_isNaN(browserToleranceCount))
      return browserToleranceCount;
    return undefined;
  }, [browserToleranceCount]);

  const decrementCount = useCallback(
    async (isIframeFocusTriggered = false) => {
      if (systemCheckRef.current && !leaveTestViolationRef.current) return;

      if (isAutoSubmitInProcess.current) {
        window.removeEventListener('focus', decrementCount, false);
        return;
      }
      const isPodbasedProblem = sessionStorage.getItem(
        DPJ_TEST_TYPE_SESSIONSTORAGE_KEY
      );
      const focusTriggerCriteria =
        isPodbasedProblem === 'true'
          ? !window.isLastEventTriggeredFromIframe &&
            window.lastEvent === 'blur'
          : true;
      // we need to have a strict check on isIframeFocusTriggered
      // Because while decrement count trigger from focus event
      // isIframeFocusTriggered will have the event data
      if (focusTriggerCriteria || isIframeFocusTriggered === true) {
        if (test.is_expired) return;
        const name = auth?.user?.first_name;
        showBrowserToleranceModal();

        if (leaveTestViolationRef.current) {
          dispatch(leaveTestViolationAction(false));
        }
        if (toleranceCount.current >= 0) {
          await decrementBrowserToleranceCount(test.slug);
          const tabId = sessionStorage.getItem(
            PROCTOR_TABID_SESSIONSTORAGE_KEY
          );

          dispatch(
            emitWebsocketEvent({
              eventName: SOCKET_EVENTS.INRCREMENT_BROWSER_TOLERANCE,
              assessmentId: testSolutionSet.id,
              assessmentType: 'test',
              token: testSolutionSet.token,
              eventGuid: v4(),
              eventCreatedAt: getServerTime().toISOString(),
              tab_id: tabId,
            })
          );
        }

        if (toleranceCount.current >= 0) {
          setModalConfig(
            () =>
              new ConfirmModel({
                onClose: () => {
                  hideBrowserToleranceModal();
                  clearClipboard();
                  if (isFullScreenDisabled())
                    setTimeout(() => {
                      setFullScreen();
                    }, 100);
                  popUpClick({
                    payload: {
                      popUpTitle: POPUP_NAME.fullScreenEnabled,
                      popUpAction: `Full Screen prompt due to tolerance violation=${actualBrowserToleranceCount ||
                        0}, remainingCount=${toleranceCount?.current}`,
                      label: 'Enable Full Screen',
                    },
                  });
                  setModalConfig(config => ({ ...config, isOpen: false }));
                },
                text: `You had navigated away from the ${assessmentType} screen. This incident will be reported to the ${assessmentType} administrator. ${
                  showWarning
                    ? `You will get a maximum of ${toleranceCount.current} more warnings before terminating your ${assessmentType} session!`
                    : ''
                }`,
                isOpen: true,
                title: `Hi ${name}!`,
                centered: true,
                showCancel: false,
                clickOutsideToClose: false,
                positiveBtnText: isFullScreenDisabled()
                  ? 'Enable fullscreen'
                  : 'Continue',
              })
          );
          popUpView({
            payload: {
              popUpTitle: POPUP_NAME.fullScreenEnabled,
              popUpAction: `Full Screen prompt due to tolerance violation=${actualBrowserToleranceCount ||
                0}, remainingCount=${toleranceCount?.current}`,
            },
          });
        }
        // Add BT auto submit pop up
        else {
          const timeSpentInTestInHumanFormat = getTimeInHumanFormat(
            getTimeSpentInTest(
              isIframeFocusTriggered
                ? sessionStorage.getItem(TEST_START_TIME_SESSIONSTORAGE_KEY)
                : startTime
            )
          );
          setModalConfig(
            () =>
              new ConfirmModel({
                onClose: () => {
                  submitTest(true);
                  if (timerRef.current) clearTimeout(timerRef.current);
                  popUpClick({
                    payload: {
                      popUpTitle: POPUP_NAME.autoSubmit,
                      popUpAction: `Section Auto Submit prompt due to browser tolerance violation=${actualBrowserToleranceCount ||
                        0}`,
                      cta: 'Procced to next page',
                    },
                  });
                  setModalConfig(config => ({ ...config, isOpen: false }));
                },
                text: (
                  <div>
                    This {assessmentType} has been auto-submitted as you did not
                    adhere to the {assessmentType} instructions. <br /> <br />
                    {!isAiInterview && (
                      <>
                        You attempted{' '}
                        <strong>{attemptedProblemsCount.current}</strong> out of{' '}
                        <strong>{_get(test, 'problems_total')}</strong>{' '}
                        {pluralize('question', _get(test, 'problems_total'))} in{' '}
                        <strong>{timeSpentInTestInHumanFormat}</strong>
                      </>
                    )}
                  </div>
                ),
                isOpen: true,
                centered: true,
                escapeToClose: false,
                clickOutsideToClose: false,
                title: 'Thank you!',
                positiveBtnText: `Proceed to ${
                  is_feedback_enabled ? 'feedback' : 'next'
                } page`,
              })
          );
          popUpView({
            payload: {
              popUpTitle: POPUP_NAME.autoSubmit,
              popUpAction: `Section Auto Submit prompt due to browser tolerance violation=${actualBrowserToleranceCount ||
                0}`,
            },
          });

          if (!timerRef.current) {
            dispatch(
              setAutoSubmitReason(
                AUTO_SUBMIT_REASONS.BROWSER_TOLERANCE_VIOLATION
              )
            );
            const currentTestEndTime = getCurrentTime().toISOString();
            if (assessmentIdRef.current) {
              dispatch(
                setTestEndTime(assessmentIdRef.current, currentTestEndTime)
              );
            }
            timerRef.current = setTimeout(() => {
              submitTest(true);
              setModalConfig(config => ({ ...config, isOpen: false }));
            }, 8000);
            window.removeEventListener('focus', decrementCount, false);
          }
        }
      }
    },
    [
      test,
      auth,
      testSolutionSet,
      attemptedProblems,
      startTime,
      isAutoSubmitInProcess.current,
      assessmentIdRef.current,
      toleranceCount.current,
      systemCheckRef.current,
      leaveTestViolationRef.current,
    ]
  );

  const handleIframePostMessage = event => {
    if (event.data === 'triggerFocus') {
      if (
        !window.isLastEventTriggeredFromParent &&
        window.lastEvent === 'blur'
      ) {
        dispatch(trackWindowFocus());
        decrementCount(true);
        window.lastEvent = 'focus';
      }
      window.isLastEventTriggeredFromParent = false;
      window.isLastEventTriggeredFromIframe = true;
    }
    if (event.data === 'triggerBlur') {
      if (isFirefoxBrowser()) {
        setTimeout(() => {
          if (!document.hasFocus() && window.lastEvent !== 'blur') {
            dispatch(trackWindowBlur());
            window.lastEvent = 'blur';
          }
          window.isLastEventTriggeredFromParent = false;
          window.isLastEventTriggeredFromIframe = true;
        }, 0);
      } else {
        if (!document.hasFocus() && window.lastEvent !== 'blur') {
          dispatch(trackWindowBlur());
          window.lastEvent = 'blur';
        }
        window.isLastEventTriggeredFromParent = false;
        window.isLastEventTriggeredFromIframe = true;
      }
    }
  };

  useEffect(() => {
    // Add a message event listener
    window.addEventListener('message', handleIframePostMessage, false);
  }, []);

  useEffect(() => {
    if (proctorEnabled) {
      const count = _get(test, 'settings.proctor.browser_tolerance.count');
      initializeBrowserTolerance(count, test.slug);
      window.addEventListener('focus', decrementCount, false);
    }
  }, [proctorEnabled]);

  useEffect(() => {
    if (is_expired) {
      window.removeEventListener('focus', decrementCount, false);
      window.removeEventListener('message', handleIframePostMessage, false);
    }
  }, [is_expired]);

  useEffect(() => {
    if (leaveTestViolation && toleranceCount.current !== undefined) {
      decrementCount();
    }
  }, [leaveTestViolation]);

  return <>{!showMultiSession && <ConfirmModal config={modalConfig} />}</>;
}
